import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';

/**
 * Full-screen camera QR scanner. Uses the browser's built-in BarcodeDetector where it exists and
 * falls back to decoding video frames with jsQR (iOS Safari). Calls onResult(text) once with the
 * first code read; `error` (from the parent) is shown over the viewfinder and scanning continues.
 */
export default function QrScanner({ onResult, onClose, error }) {
  const videoRef = useRef(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const [problem, setProblem] = useState('');

  useEffect(() => {
    let stream = null;
    let raf = 0;
    let stopped = false;
    let last = '';
    let lastAt = 0;
    const canvas = document.createElement('canvas');
    const detector = 'BarcodeDetector' in window ? new window.BarcodeDetector({ formats: ['qr_code'] }) : null;

    const report = (text) => {
      // The same code stays in view for many frames: report it once, then again only after a pause.
      const now = Date.now();
      if (!text || (text === last && now - lastAt < 2500)) return;
      last = text;
      lastAt = now;
      onResultRef.current(text);
    };

    const tick = async () => {
      if (stopped) return;
      const video = videoRef.current;
      if (video && video.readyState >= 2 && video.videoWidth) {
        try {
          if (detector) {
            const codes = await detector.detect(video);
            if (codes[0]?.rawValue) report(codes[0].rawValue);
          } else {
            const scale = Math.min(1, 640 / video.videoWidth);
            canvas.width = Math.round(video.videoWidth * scale);
            canvas.height = Math.round(video.videoHeight * scale);
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(image.data, image.width, image.height, { inversionAttempts: 'dontInvert' });
            if (code?.data) report(code.data);
          }
        } catch { /* a frame that cannot be read is simply skipped */ }
      }
      raf = requestAnimationFrame(tick);
    };

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setProblem('This browser cannot open the camera.'); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
        raf = requestAnimationFrame(tick);
      } catch (e) {
        setProblem(e?.name === 'NotAllowedError' ? 'Camera access was blocked. Allow the camera for this site and try again.' : 'No camera could be opened on this device.');
      }
    })();

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="chargeos-scanner" role="dialog" aria-modal="true" aria-label="Scan charger QR code" id="qr-scanner">
      <video ref={videoRef} playsInline muted className="chargeos-scanner-video" />
      <div className="chargeos-scanner-frame" aria-hidden="true" />
      <div className="chargeos-scanner-top">
        <strong>Scan the QR code on the charger</strong>
        <button type="button" className="btn-close btn-close-white" aria-label="Close scanner" id="btn-close-scanner" onClick={onClose} />
      </div>
      <div className="chargeos-scanner-bottom">
        {problem || error
          ? <div className="chargeos-scanner-msg error" role="alert" id="qr-scanner-error">{problem || error}</div>
          : <div className="chargeos-scanner-msg">Point the camera at the sticker next to the connector.</div>}
      </div>
    </div>
  );
}
